import { Link } from '@tanstack/react-router';
import { KeyRound } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { skillAvailability, useAiCapabilities } from '../use-ai-capabilities';

/**
 * Hộp "Cần key AI" — người dùng yêu cầu 2026-09-25.
 *
 * ## Vì sao nút AI luôn hiện, kể cả khi chưa có key
 *
 * Trước đây `AiAction` và menu "Sửa nhanh" của tab Công việc **ẩn hẳn** khi
 * chưa cấu hình AI. Hệ quả: người mới không biết tính năng tồn tại, và AI tour
 * chiếu sáng vào một chỗ trống. Nay nút luôn hiện; bấm khi chưa có key thì hộp
 * này nói rõ cần gì, chỉ chỗ nhập key, và đưa ra cách làm tay (luật #6 — mọi
 * skill có `manual`).
 *
 * Không có gì màu đỏ: chưa có key **không phải lỗi**, đó là trạng thái của mọi
 * cài đặt mới (`GET /ai/capabilities` luôn 200).
 */
export function AiKeyRequiredDialog({
  skill,
  featureLabel,
  routeParams = {},
  manualAction,
  onClose,
}: {
  readonly skill: string;
  /** Tên nút người dùng vừa bấm — hộp nhắc lại đúng chữ ấy. */
  readonly featureLabel: string;
  readonly routeParams?: Record<string, string>;
  /**
   * Đường làm tay chạy **ngay tại chỗ** (APM-CH-g: "Gợi ý không AI" của Data
   * insight) — khi đường làm tay là một thao tác trên chính màn hình đang mở chứ
   * không phải một trang khác. Bấm ⇒ đóng hộp rồi chạy.
   */
  readonly manualAction?: { readonly label: string; readonly run: () => void } | undefined;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const { data } = useAiCapabilities();
  const { manual, message } = skillAvailability(skill, data);
  const manualRoute = manual?.route
    ? Object.entries(routeParams).reduce(
        (path, [key, value]) => path.replace(`$${key}`, value),
        manual.route,
      )
    : null;

  // Esc = đóng, chặn lan truyền như `AiRunConfirmDialog`: không đóng luôn hộp cha.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [onClose]);

  return createPortal(
    <div className="relative z-[60]">
      <div className="fixed inset-0 popup-scrim" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-key-required-title"
        className="fixed left-1/2 top-1/2 flex max-h-[85vh] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 flex-col gap-3 overflow-y-auto rounded-DEFAULT border border-border bg-background p-4 shadow-xl glass-popup-md on-scrim"
      >
        <div className="flex items-start gap-2">
          <KeyRound aria-hidden className="mt-0.5 size-4 shrink-0 text-primary" />
          <h2 id="ai-key-required-title" className="text-base font-semibold">
            {t('ai.keyRequired.title')}
          </h2>
        </div>

        <div className="flex flex-col gap-3 text-sm">
          <p>{t('ai.keyRequired.body', { feature: featureLabel })}</p>
          {message ? <p className="text-xs text-muted-foreground">{message}</p> : null}

          {manual ? (
            <div className="flex flex-col gap-1 rounded-DEFAULT bg-muted/50 p-3 text-muted-foreground">
              <span className="font-medium text-foreground">{t('ai.keyRequired.manual')}</span>
              <span>{manual.label}</span>
              {manualRoute ? (
                <Link to={manualRoute} className="underline underline-offset-2" onClick={onClose}>
                  {t('ai.goManual')}
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
          {manualAction ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onClose();
                manualAction.run();
              }}
            >
              {manualAction.label}
            </Button>
          ) : null}
          <Button asChild autoFocus>
            <Link to="/settings" search={{ section: 'ai-key' }} onClick={onClose}>
              <KeyRound aria-hidden />
              {t('ai.keyRequired.openSettings')}
            </Link>
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Cho những nút AI tự dựng (mục trong menu, nút trong thanh công cụ): hỏi
 * `needsKey`, và khi cần thì `ask(skill, label)` mở hộp thay vì chạy.
 *
 * ```tsx
 * const keyGate = useAiKeyGate();
 * onSelect={() => keyGate.run('bulk_edit', label, () => setBulkEditing(true))}
 * {keyGate.dialog}
 * ```
 *
 * `needsKey` là `false` trong lúc capabilities còn đang tải: bấm lúc ấy thì
 * chạy như bình thường và API tự trả lỗi có cấu trúc, còn chặn thì nút đứng im
 * một nhịp không rõ vì sao.
 */
export function useAiKeyGate(): {
  readonly needsKey: boolean;
  readonly run: (skill: string, label: string, action: () => void) => void;
  readonly dialog: ReactNode;
} {
  const { data } = useAiCapabilities();
  const needsKey = data !== undefined && !data.enabled;
  const [asking, setAsking] = useState<{ skill: string; label: string } | null>(null);

  const run = useCallback(
    (skill: string, label: string, action: () => void) => {
      if (needsKey) setAsking({ skill, label });
      else action();
    },
    [needsKey],
  );

  const dialog = asking ? (
    <AiKeyRequiredDialog
      skill={asking.skill}
      featureLabel={asking.label}
      onClose={() => setAsking(null)}
    />
  ) : null;

  return { needsKey, run, dialog };
}
